import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useRunStore } from '../state/runStore';
import { useBugReviewStore } from '../state/bugReviewStore';
import { usePreconditionsStore } from '../state/preconditionsStore';
import { useVideosStore } from '../state/videosStore';
import { useGameKbStore } from '../state/gameKbStore';
import { useAutoWorkflowStore } from '../state/autoWorkflowStore';
import { useSessionRunsStore } from '../state/sessionRunsStore';
import { useAuth } from '../auth/AuthContext';
import { ContextTopBar } from './ContextTopBar';
import { PhasesRail } from './PhasesRail';
import { useImagesAnalysisExists, useImagesCount } from '../features/dashboard/useDashboardSignals';
import { saveBoardRunSnapshot } from '../lib/boardVisit';
import { api } from '../lib/api';
import { useWebSocket } from '../hooks/useWebSocket';

/**
 * Wraps every `/runs/:id/...` route with the dashboard chrome (left phases
 * rail + sticky top bar + main outlet). The Outlet renders the active phase
 * panel or the audit log.
 */
export function DashboardLayout() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const bindUser = useSessionRunsStore((s) => s.bindUser);
  const pinRun = useSessionRunsStore((s) => s.pin);
  const {
    current,
    loadRun,
    loadTexec,
    loadEnvLinks,
    loading,
    tests,
    criticalFiles,
    texec,
    envLinks,
  } = useRunStore();
  const imagesCount = useImagesCount(id ?? null);
  const imagesAnalysisExists = useImagesAnalysisExists(id ?? null);
  const bugBundle = useBugReviewStore((s) => s.bundle);
  const loadBugReview = useBugReviewStore((s) => s.load);
  const preconditionsBundle = usePreconditionsStore((s) => s.bundle);
  const loadPreconditions = usePreconditionsStore((s) => s.load);
  const videosList = useVideosStore((s) => s.list);
  const videosRunId = useVideosStore((s) => s.runId);
  const loadVideos = useVideosStore((s) => s.load);
  const gameKbMeta = useGameKbStore((s) => s.meta);
  const gameKbRunId = useGameKbStore((s) => s.runId);
  const loadGameKb = useGameKbStore((s) => s.load);
  const loadAutoWorkflow = useAutoWorkflowStore((s) => s.load);
  const analyzingTestKeys = useRunStore((s) => s.analyzingTestKeys);
  const analyzeAllRunning = useRunStore((s) => s.analyzeAllRunning);
  const setAnalyzeAllRunning = useRunStore((s) => s.setAnalyzeAllRunning);

  useEffect(() => {
    bindUser(user?.email ?? null);
  }, [user?.email, bindUser]);

  useEffect(() => {
    if (!current) return;
    pinRun({
      runId: current.id,
      srKey: current.sr_key,
      texecKey: current.texec_key,
      gameName: current.game_name,
      studio: current.studio,
    });
  }, [current, pinRun]);

  // Mirror bulk analyze-all into the store so the left rail shows a spinner
  // even when Phase 7 is not mounted (or between individual tests).
  useEffect(() => {
    if (!id) return;
    api
      .get<{ running: boolean }>(`/api/runs/${id}/tests/analyze-all/status`)
      .then((data) => setAnalyzeAllRunning(data.running))
      .catch(() => undefined);
  }, [id, setAnalyzeAllRunning]);

  useWebSocket(id ?? null, (msg) => {
    if (!id || msg.runId !== id) return;
    if (msg.type === 'phase' && msg.phase === 'analyze-tests') {
      setAnalyzeAllRunning(msg.status === 'in_progress');
    } else if (msg.type === 'analyze-progress' && msg.current === msg.total) {
      setAnalyzeAllRunning(false);
    }
  });

  // Keep a live snapshot so leaving the run records progress for the board
  // "updates since last visit" bell.
  const leaveSnapRef = useRef<{
    runId: string;
    texecDone: number;
    texecTotal: number;
    videoAnalyzed: number;
    videoTotal: number;
    docsReady: boolean;
    bugsInReview: number;
  } | null>(null);

  useEffect(() => {
    if (id) loadRun(id);
  }, [id, loadRun]);

  useEffect(() => {
    if (id) void loadTexec(id);
  }, [id, loadTexec]);

  useEffect(() => {
    if (id) void loadEnvLinks(id);
  }, [id, loadEnvLinks]);

  useEffect(() => {
    if (id) void loadBugReview(id);
  }, [id, loadBugReview]);

  useEffect(() => {
    if (id) void loadPreconditions(id);
  }, [id, loadPreconditions]);

  useEffect(() => {
    if (id) void loadVideos(id);
  }, [id, loadVideos]);

  useEffect(() => {
    if (id) void loadGameKb(id);
  }, [id, loadGameKb]);

  useEffect(() => {
    if (id) void loadAutoWorkflow(id);
  }, [id, loadAutoWorkflow]);

  useEffect(() => {
    if (!id) {
      leaveSnapRef.current = null;
      return;
    }
    const scored = tests.filter((t) => t.ai_verdict || t.tester_verdict).length;
    const vids = videosRunId === id ? videosList : [];
    leaveSnapRef.current = {
      runId: id,
      texecDone: scored,
      texecTotal: tests.length,
      videoAnalyzed: vids.filter((v) => v.analysisExists).length,
      videoTotal: vids.length,
      docsReady: false,
      bugsInReview: bugBundle?.items.filter((b) => !b.isDone).length ?? 0,
    };
  }, [id, tests, videosList, videosRunId, bugBundle]);

  useEffect(() => {
    return () => {
      const snap = leaveSnapRef.current;
      if (!snap) return;
      saveBoardRunSnapshot(snap.runId, {
        texecDone: snap.texecDone,
        texecTotal: snap.texecTotal,
        videoAnalyzed: snap.videoAnalyzed,
        videoTotal: snap.videoTotal,
        docsReady: snap.docsReady,
        bugsInReview: snap.bugsInReview,
      });
    };
  }, [id]);

  if (!id) return <Navigate to="/" replace />;
  if (loading && !current) {
    return (
      <div className="dashboard-loading">
        <p className="muted">Loading run...</p>
      </div>
    );
  }
  if (!current) {
    return (
      <div className="dashboard-loading">
        <p className="muted">Run not found.</p>
      </div>
    );
  }

  const unanalyzed = tests.filter((t) => !t.ai_verdict && !t.tester_verdict).length;
  const envLinksReady = envLinks
    ? {
        total: envLinks.jurisdictions.length,
        ready: envLinks.jurisdictions.filter((j) => j.status === 'ok').length,
        recipeStatus: envLinks.recipeStatus,
      }
    : null;
  const bugReviewReady = bugBundle
    ? {
        total: bugBundle.items.length,
        open: bugBundle.items.filter((b) => !b.isDone).length,
      }
    : null;
  const preconditionsReady = preconditionsBundle
    ? {
        tpKey: preconditionsBundle.tpKey,
        total: preconditionsBundle.items.length,
        done: preconditionsBundle.items.filter((i) => {
          const stage = preconditionsBundle.staged[i.id];
          const status = stage?.status ?? i.status;
          return status === 'done' || status === 'skipped';
        }).length,
        staged: Object.values(preconditionsBundle.staged).filter((s) => s.status !== undefined).length,
      }
    : null;
  const videosReady =
    videosRunId === id
      ? {
          total: videosList.length,
          extracted: videosList.filter((v) => v.frameCount > 0).length,
          analysed: videosList.filter((v) => v.analysisExists).length,
          extracting: videosList.some((v) => v.extractInProgress),
          analyzing: videosList.some((v) => v.analyzeInProgress),
        }
      : null;
  const gameKbReady =
    gameKbRunId === id
      ? { description: gameKbMeta.description.status, report: gameKbMeta.report.status }
      : null;

  return (
    <div className="dashboard-shell">
      <PhasesRail
        run={current}
        unanalyzed={unanalyzed}
        totalTests={tests.length}
        imagesCount={imagesCount}
        imagesAnalysisExists={imagesAnalysisExists}
        criticalFilesLoaded={!!criticalFiles && criticalFiles.total > 0}
        criticalFilesMissing={criticalFiles?.missing ?? 0}
        texecLoaded={!!texec}
        envLinksReady={envLinksReady}
        bugReviewReady={bugReviewReady}
        preconditionsReady={preconditionsReady}
        videosReady={videosReady}
        gameKbReady={gameKbReady}
        testsAnalyzing={analyzingTestKeys.length > 0 || analyzeAllRunning}
      />
      <div className="dashboard-main">
        <ContextTopBar run={current} />
        <div className="dashboard-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
